import { emptyProgress, type CourseProgress, type ProgressStore } from "./ProgressStore";

const STORAGE_PREFIX = "ioc.progress.";

function storageKey(courseId: string): string {
  return `${STORAGE_PREFIX}${courseId}`;
}

/**
 * Fortschritt im localStorage des Add-in-Panels.
 *
 * Office-Add-ins laufen in einem eingebetteten Browser, der localStorage
 * unterstützt. Ist der Zugriff blockiert (z. B. strenge Gruppenrichtlinien),
 * fällt der Speicher stillschweigend auf eine In-Memory-Variante zurück, damit
 * der Kurs zumindest innerhalb der Sitzung funktioniert.
 */
export class LocalProgressStore implements ProgressStore {
  private readonly memory = new Map<string, CourseProgress>();

  async load(courseId: string): Promise<CourseProgress> {
    const cached = this.memory.get(courseId);
    if (cached) return cached;

    try {
      const raw = window.localStorage.getItem(storageKey(courseId));
      if (!raw) return { ...emptyProgress };
      const parsed = JSON.parse(raw) as Partial<CourseProgress>;
      return {
        completedSteps: Array.isArray(parsed.completedSteps) ? parsed.completedSteps : [],
        lastLessonKey: parsed.lastLessonKey,
        updatedAt: parsed.updatedAt,
      };
    } catch {
      return { ...emptyProgress };
    }
  }

  async save(courseId: string, progress: CourseProgress): Promise<void> {
    const withTimestamp: CourseProgress = { ...progress, updatedAt: new Date().toISOString() };
    this.memory.set(courseId, withTimestamp);
    try {
      window.localStorage.setItem(storageKey(courseId), JSON.stringify(withTimestamp));
    } catch {
      // Nur In-Memory – der Fortschritt geht beim Schließen des Panels verloren.
    }
  }

  async reset(courseId: string): Promise<void> {
    this.memory.delete(courseId);
    try {
      window.localStorage.removeItem(storageKey(courseId));
    } catch {
      // Nichts zu tun.
    }
  }
}
